// A class's progress and released grades, by student and by assignment, with CSV and
// XLSX export. Everyone with the class's teacher view sees it: its teachers, school
// admins and anyone the class is shared with.
import React, { useCallback, useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { createLogger } from '../../utils/logger';
import { uiLocale } from '../../i18n/language';
import { ClassReport, ExportFormat, downloadClassReport, fetchClassReport } from '../../services/classService';
import ReportSummary, { formatAverage } from './ReportSummary';
import {
  cardStyle,
  errorMessage,
  errorStyle,
  hintStyle,
  linkButtonStyle,
  secondaryButtonStyle,
  sectionHeadingStyle,
  tableStyle,
  tdStyle,
  thStyle,
} from './classesStyles';

const logger = createLogger('ClassProgressSection');

interface Props {
  classId: string;
}

const ClassProgressSection: React.FC<Props> = ({ classId }) => {
  const { t } = useTranslation();
  const [report, setReport] = useState<ClassReport | null>(null);
  const [view, setView] = useState<'students' | 'assignments'>('students');
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try {
      setReport(await fetchClassReport(classId));
    } catch (err) {
      logger.error('Could not load the class report', err);
      setError(errorMessage(err, t('studentAdmin.report.couldNotLoad')));
    }
  }, [classId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const exportAs = async (format: ExportFormat) => {
    setExporting(true);
    setError(null);
    try {
      await downloadClassReport(classId, format, `class-report-${report?.class_name ?? classId}`);
    } catch (err) {
      logger.error('Could not export the class report', err);
      setError(errorMessage(err, t('studentAdmin.report.couldNotExport')));
    } finally {
      setExporting(false);
    }
  };

  if (!report) return error ? <div style={errorStyle} role="alert">{error}</div> : null;

  const progressCells = (row: { completed: number; in_progress: number; not_started: number; average_score?: number | null }) => (
    <>
      <td style={tdStyle}>{row.completed}</td>
      <td style={tdStyle}>{row.in_progress}</td>
      <td style={tdStyle}>{row.not_started}</td>
      <td style={tdStyle}>{formatAverage(row.average_score)}</td>
    </>
  );
  const progressHeaders = (
    <>
      <th style={thStyle}>{t('studentAdmin.report.completed')}</th>
      <th style={thStyle}>{t('studentAdmin.report.inProgress')}</th>
      <th style={thStyle}>{t('studentAdmin.report.notStarted')}</th>
      <th style={thStyle}>{t('studentAdmin.report.averageScore')}</th>
    </>
  );

  return (
    <section id="tour-class-progress" style={cardStyle} aria-labelledby="class-progress-heading">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 id="class-progress-heading" style={{ ...sectionHeadingStyle, margin: 0 }}>
          {t('studentAdmin.report.classTitle')}
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['csv', 'xlsx'] as ExportFormat[]).map(format => (
            <button key={format} type="button" style={secondaryButtonStyle} disabled={exporting} onClick={() => exportAs(format)}>
              <Download size={14} /> {t(`studentAdmin.report.export.${format}`)}
            </button>
          ))}
        </div>
      </div>
      <p style={hintStyle}>{t('studentAdmin.report.averageHint')}</p>
      {error && <div style={{ ...errorStyle, marginTop: '0.75rem' }} role="alert">{error}</div>}

      <ReportSummary
        counts={report}
        extra={[
          { label: t('classes.students'), value: report.students },
          { label: t('studentAdmin.report.assignments'), value: report.assignments },
        ]}
      />

      {report.assignments === 0 ? (
        <p style={{ ...hintStyle, margin: 0 }}>{t('studentAdmin.report.noAssignments')}</p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
            {(['students', 'assignments'] as const).map(name => (
              <button
                key={name}
                type="button"
                aria-pressed={view === name}
                style={{ ...linkButtonStyle, textDecoration: view === name ? 'underline' : 'none' }}
                onClick={() => setView(name)}
              >
                {t(`studentAdmin.report.by.${name}`)}
              </button>
            ))}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              {view === 'students' ? (
                <>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t('common.name')}</th>
                      {progressHeaders}
                    </tr>
                  </thead>
                  <tbody>
                    {report.by_student.map(row => (
                      <tr key={row.student_id}>
                        <td style={tdStyle}>{row.display_name || row.email || row.student_id}</td>
                        {progressCells(row)}
                      </tr>
                    ))}
                  </tbody>
                </>
              ) : (
                <>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t('studentAdmin.report.assignment')}</th>
                      <th style={thStyle}>{t('studentAdmin.report.dueDate')}</th>
                      {progressHeaders}
                    </tr>
                  </thead>
                  <tbody>
                    {report.by_assignment.map(row => (
                      <tr key={row.assignment_id}>
                        <td style={tdStyle}>{row.title}</td>
                        <td style={tdStyle}>{row.due_date ? new Date(row.due_date).toLocaleDateString(uiLocale()) : '—'}</td>
                        {progressCells(row)}
                      </tr>
                    ))}
                  </tbody>
                </>
              )}
            </table>
          </div>
        </>
      )}
    </section>
  );
};

export default ClassProgressSection;
